import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { HardHat, Plus, ShieldAlert, FileWarning, FileCheck2, AlertTriangle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

function timeAgoUTCExpiry(iso) {
  if (!iso) return null;
  const exp = new Date(iso);
  const now = new Date();
  const days = Math.round((exp - now) / 86400000);
  return days;
}

function StatusPills({ company }) {
  const ramsOk = !!company.rams_file_id;
  const insuranceDays = timeAgoUTCExpiry(company.insurance_exp_at);
  const insuranceStatus =
    insuranceDays == null ? "missing" : insuranceDays < 0 ? "expired" : insuranceDays <= 7 ? "danger" : insuranceDays <= 30 ? "warn" : "ok";

  return (
    <div className="flex flex-wrap gap-2">
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
          ramsOk ? "bg-success-50 text-success-700" : "bg-danger-50 text-danger-700"
        }`}
        data-testid="company-rams-pill"
      >
        {ramsOk ? <FileCheck2 className="w-3.5 h-3.5" /> : <FileWarning className="w-3.5 h-3.5" />}
        RAMS {ramsOk ? "on file" : "missing"}
      </span>
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
          insuranceStatus === "ok"
            ? "bg-success-50 text-success-700"
            : insuranceStatus === "warn"
            ? "bg-warning-50 text-warning-700"
            : insuranceStatus === "danger" || insuranceStatus === "expired"
            ? "bg-danger-50 text-danger-700"
            : "bg-navy-50 text-navy-600"
        }`}
        data-testid="company-insurance-pill"
      >
        <ShieldAlert className="w-3.5 h-3.5" />
        {insuranceStatus === "ok" && `Insurance — ${insuranceDays}d`}
        {insuranceStatus === "warn" && `Insurance — ${insuranceDays}d (renew)`}
        {insuranceStatus === "danger" && `Insurance — ${insuranceDays}d ⚠`}
        {insuranceStatus === "expired" && `Insurance expired`}
        {insuranceStatus === "missing" && "Insurance missing"}
      </span>
    </div>
  );
}

export default function Contractors() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contractor-companies");
      setCompanies(data?.items ?? data ?? []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const onCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      await api.post("/contractor-companies", { name: name.trim() });
      toast.success("Contractor company created");
      setName("");
      await refresh();
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    } finally {
      setCreating(false);
    }
  };

  return (
    <AppShell>
      <div className="space-y-6" data-testid="contractors-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-navy-900 tracking-tight flex items-center gap-2">
              <HardHat className="w-7 h-7 text-accent" /> Contractor companies
            </h1>
            <p className="text-sm text-navy-500 mt-1">
              Manage contractor companies, their RAMS, insurance, induction, permits, and individual contractors.
            </p>
          </div>
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-accent hover:bg-accent/90 text-white" data-testid="new-company-btn">
                <Plus className="w-4 h-4 mr-1.5" /> New company
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>New contractor company</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <label className="text-sm font-medium text-navy-700">Company name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-navy-200 rounded-lg"
                  placeholder="Acme Roofing Ltd"
                  data-testid="new-company-name"
                />
              </div>
              <DialogFooter>
                <Button
                  onClick={async () => {
                    const ok = await onCreate();
                    if (ok) document.querySelector("[data-radix-dialog-close]")?.click();
                  }}
                  disabled={creating || !name.trim()}
                  className="bg-accent hover:bg-accent/90 text-white"
                  data-testid="new-company-submit"
                >
                  Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-navy-500">Loading…</div>
        ) : companies.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-navy-200 p-10 text-center text-navy-500">
            No contractor companies yet. Click "New company" to add one.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="contractor-companies-grid">
            {companies.map((c) => {
              const days = timeAgoUTCExpiry(c.insurance_exp_at);
              const banner =
                days != null && days < 0
                  ? { tone: "danger", text: "Insurance expired — renew now" }
                  : days != null && days <= 7
                  ? { tone: "danger", text: `Insurance expires in ${days} day${days === 1 ? "" : "s"}` }
                  : days != null && days <= 30
                  ? { tone: "warn", text: `Insurance expires in ${days} days` }
                  : null;
              return (
                <Link
                  key={c.id}
                  to={`/contractors/${c.id}`}
                  className="group rounded-2xl bg-white border border-navy-100 hover:border-accent hover:shadow-lg transition-all p-5 flex flex-col gap-4"
                  data-testid={`company-card-${c.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-navy-900 truncate">{c.name}</h3>
                      {c.primary_contact?.email && (
                        <div className="text-xs text-navy-500 truncate">{c.primary_contact.email}</div>
                      )}
                    </div>
                    <ArrowRight className="w-4 h-4 text-navy-300 group-hover:text-accent transition-colors" />
                  </div>

                  {banner && (
                    <div
                      className={`flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-lg ${
                        banner.tone === "danger"
                          ? "bg-danger-50 text-danger-700"
                          : "bg-warning-50 text-warning-700"
                      }`}
                      data-testid={`company-insurance-banner-${c.id}`}
                    >
                      <AlertTriangle className="w-4 h-4" />
                      {banner.text}
                    </div>
                  )}

                  <StatusPills company={c} />
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
